import {Box, Button, Card, CardContent, Chip, Paper, Typography} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';

import type {SectionSearchHit} from '../../types';
import {snippetParts} from '../../utils/snippet';

export interface SearchResultsProps {
    /** The sections the keyword search found, best first */
    results: SectionSearchHit[];
    /** Original search query (for the empty state) */
    searchQuery: string;
    /** Called when back to browser is clicked */
    onClear: () => void;
}

/**
 * The sections a keyword search found, each a link to the section with the fragment that matched.
 */
export function SearchResults({results, searchQuery, onClear}: SearchResultsProps) {
    return (
        <Card sx={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'fadeIn 0.3s cubic-bezier(0.25, 1, 0.5, 1)'}}>
            <CardContent>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                    <Typography variant="h5">
                        Search Results
                    </Typography>
                    <Button size="small" onClick={onClear}>
                        Back to Browser
                    </Button>
                </Box>

                {results.length === 0 ? (
                    <Typography variant="body1" sx={{color: 'text.secondary'}}>
                        No sections match "{searchQuery}"
                    </Typography>
                ) : (
                    <Box sx={{maxHeight: '70vh', overflow: 'auto'}}>
                        {results.map((hit) => {
                            const title = [hit.number, hit.heading].filter(Boolean).join(' ');
                            return (
                                <Paper
                                    key={hit.section_id}
                                    component={RouterLink}
                                    to={`/docs/${hit.section_id}`}
                                    aria-label={title}
                                    sx={{
                                        display: 'block',
                                        p: 2,
                                        mb: 2,
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        transition: 'transform 0.15s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.15s cubic-bezier(0.25, 1, 0.5, 1), background-color 0.15s cubic-bezier(0.25, 1, 0.5, 1)',
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                            transform: 'translateY(-1px)',
                                            boxShadow: 2,
                                        },
                                        '&:focus-visible': {
                                            outline: '2px solid',
                                            outlineColor: 'primary.main',
                                            outlineOffset: 2,
                                        },
                                    }}
                                >
                                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1}}>
                                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', flex: 1, minWidth: 0}}>
                                            {title}
                                        </Typography>
                                        <Chip label={hit.doc_id} size="small" variant="outlined" sx={{fontSize: '0.7rem'}}/>
                                    </Box>
                                    <Typography variant="body2" sx={{color: 'text.primary', whiteSpace: 'pre-wrap'}}>
                                        {snippetParts(hit.snippet).map((part, i) => part.match ? (
                                            <Box
                                                key={i}
                                                component="mark"
                                                sx={{bgcolor: 'warning.light', color: 'inherit', px: 0.25, borderRadius: 0.5}}
                                            >
                                                {part.text}
                                            </Box>
                                        ) : (
                                            <span key={i}>{part.text}</span>
                                        ))}
                                    </Typography>
                                </Paper>
                            );
                        })}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

export default SearchResults;
